// src/components/Studio/StudioStates.tsx
import type { ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { PANEL, SECONDARY_BUTTON } from './studioStyles';

/**
 * Loading / error / empty states for the studio surfaces.
 *
 * The error state takes a retry callback rather than reloading the window, so a failed
 * fetch doesn't throw away scroll position, filters or an in-progress form.
 */

export const StudioSkeleton = ({
  count = 6,
  aspect = 'aspect-[2/3]',
}: {
  count?: number;
  aspect?: string;
}) => (
  <div
    className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4"
    role="status"
    aria-label="Loading"
  >
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className={`${PANEL} overflow-hidden`}>
        <div className={`${aspect} animate-pulse bg-white/[0.06]`} />
        <div className="space-y-2 p-4">
          <div className="h-4 w-3/4 animate-pulse rounded bg-white/[0.06]" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-white/[0.04]" />
        </div>
      </div>
    ))}
    <span className="sr-only">Loading…</span>
  </div>
);

export const StudioErrorState = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) => (
  <div className={`${PANEL} px-6 py-14 text-center`} role="alert">
    <AlertTriangle className="mx-auto h-8 w-8 text-orange-400" aria-hidden="true" />
    <p className="mt-4 font-display text-lg font-semibold text-white">That didn&apos;t load</p>
    <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">{message}</p>
    {onRetry && (
      <button type="button" onClick={onRetry} className={`${SECONDARY_BUTTON} mt-6`}>
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        Try again
      </button>
    )}
  </div>
);

export const StudioEmptyState = ({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) => (
  <div className={`${PANEL} px-6 py-16 text-center`}>
    {icon && <div className="mx-auto mb-4 flex justify-center text-orange-400/80">{icon}</div>}
    <p className="font-display text-lg font-semibold text-white">{title}</p>
    <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-neutral-400">{description}</p>
    {action && <div className="mt-7 flex justify-center">{action}</div>}
  </div>
);

export const StudioCallout = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) => (
  <div className={`${PANEL} flex flex-col items-start gap-6 p-8 sm:flex-row sm:items-center sm:justify-between`}>
    <div className="max-w-xl">
      <p className="font-display text-xl font-bold text-white">{title}</p>
      <p className="mt-2 text-sm leading-relaxed text-neutral-400">{description}</p>
    </div>
    {action}
  </div>
);
